import {create} from 'zustand';
import {persist} from 'zustand/middleware';
import {
  DashboardUser,
  CreateUserRequest,
  UpdateUserRequest,
  UpdateUserPasswordRequest,
  AssignRoleRequest,
  RemoveRoleRequest,
  UserFilters,
  UserStatistics,
  UserStatus,
  AssignPermissionsRequest
} from '../models/user.model';
import {UserRepository} from '../repository/UserRepository';
import {userFactory} from './userFactory';

interface UserState {
  // Data
  users: DashboardUser[];
  currentUser: DashboardUser | null;
  statistics: UserStatistics | null;
  pagination: any;

  // Loading states
  loading: boolean;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  fetchingStatistics: boolean;

  // Error states
  error: string | null;
  createError: string | null;
  updateError: string | null;
  deleteError: string | null;

  // Filters
  filters: UserFilters;

  // Actions
  fetchUsers: (filters?: UserFilters) => Promise<void>;
  fetchAdmins: () => Promise<void>;
  createUser: (userData: CreateUserRequest) => Promise<DashboardUser>;
  updateUser: (userData: UpdateUserRequest) => Promise<DashboardUser>;
  deleteUser: (userId: number) => Promise<void>;
  assignRole: (roleData: AssignRoleRequest) => Promise<DashboardUser>;
  removeRole: (roleData: RemoveRoleRequest) => Promise<DashboardUser>;
  activateUser: (userId: number) => Promise<DashboardUser>;
  deactivateUser: (userId: number) => Promise<DashboardUser>;
  updateUserPassword: (passwordData: UpdateUserPasswordRequest) => Promise<void>;
  fetchStatistics: () => Promise<void>;
  assignUserPermissions: (userId: number, permissions: string[]) => Promise<void>;

  // Utility actions
  setFilters: (filters: Partial<UserFilters>) => void;
  clearError: () => void;
  clearCreateError: () => void;
  clearUpdateError: () => void;
  clearDeleteError: () => void;
  reset: () => void;
}

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => {
      const {userRepository} = userFactory.getRepository();

      return {
        // Initial state
        users: [],
        currentUser: null,
        statistics: null,
        pagination: null,

        loading: false,
        creating: false,
        updating: false,
        deleting: false,
        fetchingStatistics: false,

        error: null,
        createError: null,
        updateError: null,
        deleteError: null,

        filters: {
          page: 1,
          limit: 10
        },

        // Actions
        fetchUsers: async (filters?: UserFilters) => {
          set({loading: true, error: null});

          try {
            const currentFilters = {...get().filters, ...filters};
            const result = await userRepository.getUsers(currentFilters);

            set({
              users: result.users,
              pagination: result.pagination,
              filters: currentFilters,
              loading: false
            });
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to fetch users';
            set({error: message, loading: false});
            throw error;
          }
        },

        fetchAdmins: async () => {
          set({loading: true, error: null});

          try {
            const result = await userRepository.getAdmins();

            set({
              users: result.users,
              pagination: result.pagination,
              loading: false
            });
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to fetch admins';
            set({error: message, loading: false});
            throw error;
          }
        },

        createUser: async (userData: CreateUserRequest) => {
          set({creating: true, createError: null});

          try {
            const newUser = await userRepository.createUser(userData);

            set((state) => ({
              users: [newUser, ...state.users],
              creating: false
            }));

            return newUser;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to create user';
            set({createError: message, creating: false});
            throw error;
          }
        },

        updateUser: async (userData: UpdateUserRequest) => {
          set({updating: true, updateError: null});

          try {
            const updatedUser = await userRepository.updateUser(userData);

            set((state) => ({
              users: state.users.map(user =>
                user.id === updatedUser.id ? updatedUser : user
              ),
              updating: false
            }));

            return updatedUser;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to update user';
            set({updateError: message, updating: false});
            throw error;
          }
        },

        deleteUser: async (userId: number) => {
          set({deleting: true, deleteError: null});

          try {
            await userRepository.deleteUser(userId);

            set((state) => ({
              users: state.users.filter(user => user.id !== userId),
              deleting: false
            }));
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to delete user';
            set({deleteError: message, deleting: false});
            throw error;
          }
        },

        assignRole: async (roleData: AssignRoleRequest) => {
          set({updating: true, updateError: null});

          try {
            const updatedUser = await userRepository.assignRole(roleData);

            set((state) => ({
              users: state.users.map(user =>
                user.id === updatedUser.id ? updatedUser : user
              ),
              updating: false
            }));

            return updatedUser;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to assign role';
            set({updateError: message, updating: false});
            throw error;
          }
        },

        removeRole: async (roleData: RemoveRoleRequest) => {
          set({updating: true, updateError: null});

          try {
            const updatedUser = await userRepository.removeRole(roleData);

            set((state) => ({
              users: state.users.map(user =>
                user.id === updatedUser.id ? updatedUser : user
              ),
              updating: false
            }));

            return updatedUser;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to remove role';
            set({updateError: message, updating: false});
            throw error;
          }
        },

        activateUser: async (userId: number) => {
          set({updating: true, updateError: null});

          try {
            const updatedUser = await userRepository.activateUser(userId);

            set((state) => ({
              users: state.users.map(user =>
                user.id === updatedUser.id ? updatedUser : user
              ),
              updating: false
            }));

            return updatedUser;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to activate user';
            set({updateError: message, updating: false});
            throw error;
          }
        },

        deactivateUser: async (userId: number) => {
          set({updating: true, updateError: null});

          try {
            const updatedUser = await userRepository.deactivateUser(userId);

            set((state) => ({
              users: state.users.map(user =>
                user.id === updatedUser.id ? updatedUser : user
              ),
              updating: false
            }));

            return updatedUser;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to deactivate user';
            set({updateError: message, updating: false});
            throw error;
          }
        },

        updateUserPassword: async (passwordData: UpdateUserPasswordRequest) => {
          set({updating: true, updateError: null});

          try {
            await userRepository.updateUserPassword(passwordData);
            set({updating: false});
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to update password';
            set({updateError: message, updating: false});
            throw error;
          }
        },

        fetchStatistics: async () => {
          set({fetchingStatistics: true});

          try {
            const statistics = await userRepository.getUserStatistics();
            set({statistics, fetchingStatistics: false});
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to fetch statistics';
            set({error: message, fetchingStatistics: false});
            throw error;
          }
        },

        assignUserPermissions: async (userId: number, permissions: string[]) => {
          set({updating: true, updateError: null});

          try {
            // For now, we'll use the API directly since we don't have this method in UserRepository
            // This should be moved to a proper permission repository in the future
            const {userDataSource} = userFactory;
            const permissionData = {
              user_id: userId,
              permissions: permissions,
              granted_by: 1 // Default to admin - should be replaced with current user context
            };

            // This is a placeholder implementation - should be replaced with proper permission assignment

            set({updating: false});
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to assign permissions';
            set({updateError: message, updating: false});
            throw error;
          }
        },

        // Utility actions
        setFilters: (filters: Partial<UserFilters>) => {
          // Ensure only serializable data is stored
          const cleanFilters = {
            status: typeof filters.status === 'string' ? filters.status : undefined,
            roleId: typeof filters.roleId === 'number' ? filters.roleId : undefined,
            search: typeof filters.search === 'string' ? filters.search : undefined,
            page: typeof filters.page === 'number' ? filters.page : undefined,
            limit: typeof filters.limit === 'number' ? filters.limit : undefined
          };

          set((state) => ({
            filters: {...state.filters, ...cleanFilters}
          }));
        },

        clearError: () => set({error: null}),
        clearCreateError: () => set({createError: null}),
        clearUpdateError: () => set({updateError: null}),
        clearDeleteError: () => set({deleteError: null}),

        reset: () => set({
          users: [],
          currentUser: null,
          statistics: null,
          pagination: null,
          loading: false,
          creating: false,
          updating: false,
          deleting: false,
          fetchingStatistics: false,
          error: null,
          createError: null,
          updateError: null,
          deleteError: null,
          filters: {page: 1, limit: 10}
        })
      };
    },
    {
      name: 'user-store',
      partialize: (state) => ({
        filters: state.filters
      })
    }
  )
);
